import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, IndianRupee, CheckCircle2, QrCode } from 'lucide-react';
import { format } from 'date-fns';

import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();
const QR_CODE_URL = 'https://customer-assets.emergentagent.com/job_weakenders-rsvp/artifacts/gczl4the_WhatsApp%20Image%202026-02-13%20at%2016.14.04.jpeg';

function PaymentPage() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [transactionRef, setTransactionRef] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPayment();
  }, [paymentId]);

  const fetchPayment = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/payments/user`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      const foundPayment = res.data.find(p => p.payment_id === paymentId);
      if (!foundPayment) {
        toast.error('Payment not found');
        navigate('/profile');
        return;
      }
      setPayment(foundPayment);
    } catch (error) {
      toast.error('Failed to load payment');
      navigate('/profile');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!transactionRef.trim()) {
      toast.error('Please enter transaction reference');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/payments/mark-paid`,
        { payment_id: paymentId, transaction_ref: transactionRef },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Payment confirmed! Thank you.');
      navigate('/profile');
    } catch (error) {
      toast.error('Failed to mark payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!payment) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate('/profile')}
            variant="ghost"
            size="icon"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold text-[#0F172A]">Make Payment</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Payment Details */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="text-center mb-4">
            <p className="text-sm text-slate-600">Amount to Pay</p>
            <p className="text-4xl font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
          </div>
          <div className="text-center text-sm text-slate-600">
            <p className="font-semibold text-[#0F172A]">
              {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
            </p>
            <p>{payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}</p>
          </div>
        </div>

        {/* QR Code */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-[#E63946] p-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <QrCode className="w-5 h-5 text-[#E63946]" />
            <h2 className="text-lg font-bold text-[#0F172A]">Scan to Pay</h2>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <img 
              src={QR_CODE_URL}
              alt="Payment QR Code"
              className="w-full max-w-[300px] mx-auto"
              data-testid="payment-qr-code"
            />
          </div>

          <div className="text-center space-y-1">
            <p className="text-sm font-semibold text-slate-700">Account: Saketh Sistla</p>
            <p className="text-sm text-slate-600">UPI ID: 9930097428@slc</p>
          </div>
        </div>

        {/* Payment Confirmation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">After Payment</h3>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="transaction_ref" className="text-sm font-semibold text-slate-700">
                Transaction Reference / Last 4 digits
              </Label>
              <Input
                id="transaction_ref"
                placeholder="e.g., 1234 or TXN123456"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                className="mt-2"
                data-testid="transaction-ref-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter transaction ID or last 4 digits for verification
              </p>
            </div>

            <Button
              onClick={handleMarkPaid}
              disabled={submitting || !transactionRef.trim()}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold uppercase py-6 rounded-lg"
              data-testid="mark-paid-btn"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  I Have Paid
                </>
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              Your payment will be confirmed instantly after you submit
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;